import React, { useEffect, useState } from 'react';
import { useApp } from '../../context/AppContext';
import axios from 'axios';
import { Play, Clock, X } from 'lucide-react';
import { Dialog, DialogContent } from '../../components/ui/dialog';

const Videos = () => {
  const { t, API, settings } = useApp();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVideo, setSelectedVideo] = useState(null);

  // Check if embedded player is enabled (default true)
  const embedEnabled = settings?.player_settings?.videos_embed !== false;

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const response = await axios.get(`${API}/public/videos`);
        setVideos(response.data.videos || []);
      } catch (error) {
        console.error('Failed to fetch videos:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchVideos();
  }, [API]);

  const formatDuration = (seconds) => {
    if (!seconds) return '';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleVideoClick = (e, video) => {
    if (embedEnabled) {
      e.preventDefault();
      setSelectedVideo(video);
      // Track analytics
      axios.post(`${API}/public/analytics/track`, { route: `/videos/${video.video_id}` }).catch(() => {});
    }
  };

  return (
    <div className="min-h-screen bg-brand-void">
      {/* Hero */}
      <section className="pt-32 pb-16 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <h1 className="font-syne font-bold text-4xl md:text-5xl lg:text-6xl text-white mb-4 reveal" data-testid="videos-title">
            {t('videos.title', 'Music Videos')}
          </h1>
          <div className="flex items-center gap-4">
            <div className="h-1 w-16 bg-brand-gold rounded-full" />
            <p className="text-white/50 text-lg reveal" style={{ animationDelay: '0.2s' }}>
              Official music videos and visual content
            </p>
          </div>
        </div>
      </section>

      {/* Videos Grid */}
      <section className="pb-24 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="aspect-video bg-brand-charcoal animate-pulse rounded-lg" />
              ))}
            </div>
          ) : videos.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {videos.map((video, index) => (
                <a
                  key={video.video_id}
                  href={`https://www.youtube.com/watch?v=${video.video_id}`}
                  target={embedEnabled ? undefined : "_blank"}
                  rel={embedEnabled ? undefined : "noopener noreferrer"}
                  onClick={(e) => handleVideoClick(e, video)}
                  className="group reveal card-hover cursor-pointer"
                  style={{ animationDelay: `${index * 0.1}s` }}
                  data-testid={`video-card-${video.video_id}`}
                >
                  <div className="relative aspect-video rounded-lg overflow-hidden bg-brand-charcoal border border-white/5 group-hover:border-brand-gold/50 transition-colors">
                    <img
                      src={video.thumbnail}
                      alt={video.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      loading="lazy"
                    />
                    
                    {/* Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent opacity-60 group-hover:opacity-80 transition-opacity duration-300" />
                    
                    {/* Play Button */}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-20 h-20 rounded-full bg-brand-red/80 backdrop-blur flex items-center justify-center transform scale-90 group-hover:scale-100 transition-all duration-300 glow-red">
                        <Play className="w-8 h-8 text-white fill-white ml-1" />
                      </div>
                    </div>

                    {/* Duration Badge */}
                    {video.duration && (
                      <div className="absolute bottom-4 right-4 flex items-center gap-1.5 px-2 py-1 bg-black/60 backdrop-blur rounded text-white/80 text-sm">
                        <Clock className="w-3.5 h-3.5" />
                        {formatDuration(video.duration)}
                      </div>
                    )}

                    {/* Featured Badge */}
                    {video.featured && (
                      <span className="absolute top-4 left-4 px-3 py-1 bg-brand-gold text-white text-xs font-semibold rounded-full">
                        Featured
                      </span>
                    )}

                    {/* Title Overlay */}
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <h3 className="font-syne font-semibold text-xl md:text-2xl text-white line-clamp-2 group-hover:text-brand-gold transition-colors">
                        {video.title}
                      </h3>
                    </div>
                  </div>
                </a>
              ))}
            </div>
          ) : (
            <div className="text-center py-24">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-brand-dark flex items-center justify-center">
                <Play className="w-10 h-10 text-white/30" />
              </div>
              <h3 className="font-syne text-xl text-white mb-2">No Videos Yet</h3>
              <p className="text-white/50">Music videos will appear here once configured in the admin panel.</p>
            </div>
          )}
        </div>
      </section>

      {/* Video Player Modal */}
      <Dialog open={!!selectedVideo} onOpenChange={() => setSelectedVideo(null)}>
        <DialogContent className="max-w-5xl w-full p-0 bg-black border-white/10 overflow-hidden">
          <button
            onClick={() => setSelectedVideo(null)}
            className="absolute top-4 right-4 z-50 w-10 h-10 rounded-full bg-black/50 backdrop-blur flex items-center justify-center text-white hover:bg-white/20 transition-colors"
            data-testid="close-video-modal"
          >
            <X className="w-5 h-5" />
          </button>
          {selectedVideo && (
            <div className="aspect-video">
              <iframe
                src={`https://www.youtube.com/embed/${selectedVideo.video_id}?autoplay=1&rel=0`}
                title={selectedVideo.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Videos;
